<template>
    <div class="habits-stats">
        <div class="hs-toolbar" data-html2canvas-ignore>
            <tabs-group dense :tabs="scopeTabs" :value="scope" @change="setScope" />
            <div class="hs-select">
                <kimsa-combobox v-if="scope === 'students'" dense solo searchable :items="students" item-value="objectID"
                    :value="currentStudent" @change="currentStudent = $event" icon="$account"
                    :placeholder="$t('statistics.select_student_placeholder')" placeholder-class />
                <kimsa-combobox v-else-if="scope === 'groups'" dense solo searchable :items="groupItems" item-value="id"
                    :value="currentGroup" @change="currentGroup = $event" icon="$account_multiple"
                    :placeholder="$t('statistics.select_group_placeholder')" placeholder-class />
                <kimsa-combobox v-else dense solo searchable :items="teamItems" item-value="id" :value="currentTeam"
                    @change="currentTeam = $event" icon="$account_multiple"
                    :placeholder="$t('statistics.select_team_placeholder')" placeholder-class />
            </div>
            <habit-dropdown v-if="scope !== 'students' && comboItems.length > 2" :items="comboItems" :value="comboFilter"
                :min-width="220" @change="comboFilter = $event" />
            <v-btn v-if="studentAssignment" text small color="accent" class="ml-auto" @click="$refs.goals.show(studentAssignment)">
                <v-icon size="16" class="mr-1">mdi-target</v-icon>{{ $t('habits.student_goals') }}
            </v-btn>
            <button-tooltip v-if="canShare" bottom :tooltip="$t('common.share_image')" :class="{ 'ml-auto': !studentAssignment }">
                <template #activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" icon class="export-btn" :loading="loadingShare"
                        :disabled="loading || !rowStudentIds.length" @click="shareImage">
                        <v-icon size="18">mdi-share-variant-outline</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>
        </div>

        <skeleton-loader v-if="loading" type="stat-grid" />

        <div v-else-if="!scopeIds.length" class="hs-empty">
            <v-icon size="46" color="txtMuted">mdi-checkbox-marked-outline</v-icon>
            <p>{{ $t('habits.stats_select') }}</p>
        </div>

        <div v-else-if="!rowStudentIds.length" class="hs-empty">
            <v-icon size="46" color="txtMuted">mdi-checkbox-marked-outline</v-icon>
            <p>{{ $t('habits.stats_no_combo') }}</p>
            <v-btn depressed color="primary" class="black--text" @click="goLibrary">{{ $t('habits.go_library') }}</v-btn>
        </div>

        <template v-else>
            <div class="hs-kpis">
                <kpi-card v-for="kpi in kpis" :key="kpi.label" :label="kpi.label" :value="kpi.value" :delta="kpi.delta"
                    :delta-up="kpi.deltaUp" :hint="kpi.hint" :color="kpi.color" />
            </div>

            <div class="hs-grid">
                <stat-card>
                    <template #header>
                        <span>{{ $t('habits.day_by_day') }}</span>
                        <div class="hs-nav">
                            <button type="button" class="hs-arrow" :disabled="offset >= maxOffset" @click="offset++">
                                <v-icon size="16">mdi-chevron-left</v-icon>
                            </button>
                            <span class="hs-range">{{ rangeLabel }}</span>
                            <button type="button" class="hs-arrow" :disabled="offset === 0" @click="offset--">
                                <v-icon size="16">mdi-chevron-right</v-icon>
                            </button>
                        </div>
                    </template>
                    <div class="hm-scroll">
                        <table class="hm">
                            <thead>
                                <tr>
                                    <th class="hm-name"></th>
                                    <th v-for="day in visibleDates" :key="day.date" class="hm-day"
                                        :class="{ 'hm-day--today': day.date === today, 'hm-day--week': day.weekStart }">
                                        <div class="hm-letter">{{ day.md ? 'MD' : day.letter }}</div>
                                        <div class="hm-num">{{ day.num }}</div>
                                    </th>
                                    <th class="hm-pct">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in heatmapRows" :key="row.key">
                                    <td class="hm-name">
                                        <button v-if="row.studentId && scope !== 'students'" type="button" class="hm-link"
                                            @click="drill(row.studentId)">{{ row.name }}</button>
                                        <span v-else>{{ row.name }}</span>
                                    </td>
                                    <td v-for="cell in row.cells" :key="cell.date" class="hm-td">
                                        <button-tooltip :tooltip="cell.tooltip" top :disabled="!cell.tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <span class="hm-cell" :class="`hm-cell--${cell.status}`"
                                                    :style="cell.color ? { background: cell.color } : null" v-bind="attrs" v-on="on" />
                                            </template>
                                        </button-tooltip>
                                    </td>
                                    <td class="hm-pct" :class="`hs-lvl--${level(row.pct)}`">{{ row.pct === null ? '-' : `${row.pct}%` }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </stat-card>

                <stat-card>
                    <template #header>
                        <span>{{ $t('habits.evolution') }}</span>
                        <habit-dropdown v-if="metricItems.length > 1" :items="metricItems" :value="metric" :min-width="220"
                            @change="metric = $event" />
                    </template>
                    <div class="evo">
                        <svg class="evo-svg" viewBox="0 0 320 150" preserveAspectRatio="none">
                            <line v-for="y in [0, 25, 50, 75, 100]" :key="y" class="evo-grid" :class="{ 'evo-goal': y === 75 }"
                                x1="0" x2="320" :y1="yOf(y)" :y2="yOf(y)" />
                            <polyline v-if="metric !== 'general'" class="evo-line evo-line--bg" :points="points(evolutionGeneral)" />
                            <polyline class="evo-line" :points="points(evolution)" />
                            <circle v-for="(p, i) in dots(evolution)" :key="i" class="evo-dot" :cx="p.x" :cy="p.y" r="3" />
                        </svg>
                        <div class="evo-x">
                            <span v-for="week in evolutionWeeks" :key="week">{{ week }}</span>
                        </div>
                        <div class="evo-leg">
                            <span><i class="evo-leg-line"></i>{{ metricLabel }}</span>
                            <span><i class="evo-leg-goal"></i>{{ $t('habits.goal_75') }}</span>
                        </div>
                    </div>
                </stat-card>

                <stat-card class="hs-wide">
                    <template #header>
                        <span>{{ switchTitle }}</span>
                        <kimsa-segmented dense :items="switchItems" :value="switchView" @change="switchView = $event" />
                    </template>

                    <div v-if="switchView === 'habit'" class="bars">
                        <div v-for="item in byHabit" :key="item.label" class="bar-row">
                            <span class="bar-name text-truncate">
                                {{ item.label }}
                                <em v-for="tag in item.tags" :key="tag">{{ tag }}</em>
                                <v-icon v-if="item.alarm" size="12">mdi-bell-outline</v-icon>
                            </span>
                            <div class="bar-track"><div class="bar-fill" :class="`hs-bg--${level(item.value)}`" :style="{ width: `${item.value}%` }" /></div>
                            <span class="bar-val" :class="`hs-lvl--${level(item.value)}`">{{ item.value }}%</span>
                        </div>
                        <div v-if="!byHabit.length" class="hs-muted">{{ $t('statistics.no_data') }}</div>
                    </div>

                    <div v-else-if="switchView === 'weekday'" class="dow">
                        <div class="dow-bars">
                            <div v-for="item in byWeekday" :key="item.label" class="dow-col">
                                <span class="dow-val">{{ item.value === null ? '-' : `${item.value}%` }}</span>
                                <div class="dow-track">
                                    <div class="dow-fill" :class="item.worst ? 'hs-bg--bad' : `hs-bg--${level(item.value)}`"
                                        :style="{ height: `${item.value || 0}%` }" />
                                </div>
                                <span class="dow-lbl">{{ item.label }}</span>
                            </div>
                        </div>
                        <div v-if="worstDay" class="hs-muted">{{ $t('habits.worst_day', { day: worstDay.long, n: worstDay.value }) }}</div>
                    </div>

                    <div v-else class="alarms">
                        <div class="al-chips">
                            <span class="al-chip"><v-icon size="13">mdi-bell-outline</v-icon>{{ $tc('habits.alarm_chip_per_habit', alarmStats.perHabit, { n: alarmStats.perHabit }) }}</span>
                            <span class="al-chip">{{ $tc('habits.alarm_chip_daily', alarmStats.daily, { n: alarmStats.daily }) }}</span>
                            <span class="al-chip">{{ $tc('habits.alarm_chip_none', alarmStats.none, { n: alarmStats.none }) }}</span>
                        </div>
                        <div class="al-title">{{ $t('habits.when_registers') }}</div>
                        <div class="al-hist">
                            <div v-for="item in histogram" :key="item.hour" class="al-col">
                                <v-icon v-if="item.alarm" size="11" class="al-bell">mdi-bell</v-icon>
                                <div class="al-bar" :style="{ height: `${item.pct}%` }" />
                                <span class="al-h">{{ item.hour }}</span>
                            </div>
                        </div>
                    </div>
                </stat-card>
            </div>
        </template>

        <student-habit-goals-dialog ref="goals" @success="onGoalsSaved" />
    </div>
</template>

<script>
import moment from 'moment'
import { mapState, mapMutations } from 'vuex'
import { db } from '@/utils/Firebase'
import { shareNodeAsImage } from '@/utils/ShareImage'
import { UserIndex } from '@/utils/Algolia'
import {
    DATE_FORMAT, today, chunk, weekStartOf, weekDates, dayProgress, habitAppliesOn, habitScore, habitDone,
    computeWeekSummary, resolveAlarmHours, adherenceLevel, mdLabel, isAssignmentActive,
} from '@/utils/Habits'
import TabsGroup from '@/components/common/TabsGroup'
import KimsaCombobox from '@/components/common/inputs/KimsaCombobox'
import KimsaSegmented from '@/components/common/inputs/KimsaSegmented'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import KpiCard from '@/components/common/KpiCard'
import StatCard from './StatCard.vue'
import HabitDropdown from '@/components/habits/HabitDropdown.vue'
import StudentHabitGoalsDialog from '@/components/habits/StudentHabitGoalsDialog.vue'
import habitsMixin from '@/mixins/habitsMixin'

const WEEKS_LOADED = 9
const VISIBLE_WEEKS = 3
const EVOLUTION_WEEKS = 8

const mix = (a, b, t) => {
    const pa = [1, 3, 5].map(i => parseInt(a.slice(i, i + 2), 16))
    const pb = [1, 3, 5].map(i => parseInt(b.slice(i, i + 2), 16))
    return `rgb(${pa.map((v, i) => Math.round(v + (pb[i] - v) * t)).join(',')})`
}

const scoreColor = (score) => {
    if (score >= .75) return '#009966'
    if (score < .25) return '#E85C5C'
    const t = (score - .25) / .5
    return t < .5 ? mix('#E85C5C', '#E8C000', t * 2) : mix('#E8C000', '#009966', (t - .5) * 2)
}

export default {
    name: 'StatisticsHabits',
    mixins: [habitsMixin],
    components: { TabsGroup, KimsaCombobox, KimsaSegmented, SkeletonLoader, ButtonTooltip, KpiCard, StatCard, HabitDropdown, StudentHabitGoalsDialog },
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            scope: 'groups',
            students: [],
            currentStudent: null,
            currentGroup: null,
            currentTeam: null,
            comboFilter: '',
            loading: false,
            loadingShare: false,
            assignments: {},
            weeks: {},
            offset: 0,
            metric: 'general',
            switchView: 'habit',
            today: today(),
        }
    },
    computed: {
        ...mapState(['groups', 'teams']),
        scopeTabs(vm = this) {
            return [
                { label: vm.$t('common.students'), value: 'students' },
                { label: `${vm.$t('common.group')}s`, value: 'groups' },
                { label: `${vm.$t('common.team')}s`, value: 'teams' },
            ]
        },
        groupItems(vm = this) {
            return vm.groups.filter(group => !vm.coachId || group.coachId === vm.coachId)
        },
        teamItems(vm = this) {
            return vm.teams.filter(team => !vm.coachId || team.coachId === vm.coachId)
        },
        scopeIds(vm = this) {
            if (vm.scope === 'students') return vm.currentStudent?.objectID ? [vm.currentStudent.objectID] : []
            if (vm.scope === 'groups') return [...new Set((vm.currentGroup?.students || []).filter(Boolean))]
            const ids = []
            ;(vm.currentTeam?.groups || []).forEach(group => (group.students || []).forEach(id => ids.push(id)))
            return [...new Set(ids)]
        },
        studentNames(vm = this) {
            const map = {}
            vm.students.forEach(s => { map[s.objectID] = [s.name, s.lastName].filter(Boolean).join(' ') || s.email || '' })
            return map
        },
        canShare(vm = this) {
            return vm.accountType === 'coach' || vm.accountType === 'company'
        },
        scopeName(vm = this) {
            if (vm.scope === 'students') return vm.studentNames[vm.currentStudent?.objectID] || ''
            if (vm.scope === 'groups') return vm.currentGroup?.name || ''
            return vm.currentTeam?.name || ''
        },
        studentAssignment(vm = this) {
            if (vm.scope !== 'students') return null
            return vm.assignments[vm.scopeIds[0]] || null
        },
        rowStudentIds(vm = this) {
            return vm.scopeIds.filter(id => {
                const a = vm.assignments[id]
                if (!a) return false
                if (vm.comboFilter && a.comboId !== vm.comboFilter) return false
                return true
            })
        },
        comboItems(vm = this) {
            const map = {}
            vm.scopeIds.forEach(id => {
                const a = vm.assignments[id]
                if (a) map[a.comboId] = a.combo.name
            })
            return [{ value: '', text: vm.$t('habits.all_combos') }, ...Object.keys(map).map(id => ({ value: id, text: map[id] }))]
        },
        currentWeekStart(vm = this) {
            return weekStartOf(vm.today)
        },
        maxOffset() {
            return WEEKS_LOADED - VISIBLE_WEEKS
        },
        visibleWeekStarts(vm = this) {
            return Array.from({ length: VISIBLE_WEEKS }, (_, i) => moment(vm.currentWeekStart, DATE_FORMAT)
                .subtract((vm.offset + VISIBLE_WEEKS - 1 - i) * 7, 'days').format(DATE_FORMAT))
        },
        previousWeekStarts(vm = this) {
            return vm.visibleWeekStarts.map(start => moment(start, DATE_FORMAT).subtract(VISIBLE_WEEKS * 7, 'days').format(DATE_FORMAT))
        },
        visibleDates(vm = this) {
            const mdDates = new Set()
            vm.rowStudentIds.forEach(id => Object.values(vm.weeks[id] || {}).forEach(week => {
                Object.keys(week.mdDays || {}).forEach(date => { if ((week.mdDays[date] || []).includes(0)) mdDates.add(date) })
            }))
            return vm.visibleWeekStarts.flatMap(start => weekDates(start).map((date, i) => {
                const m = moment(date, DATE_FORMAT)
                return { date, weekStart: i === 0, letter: m.format('dd').charAt(0).toUpperCase(), num: m.format('D'), md: mdDates.has(date) }
            }))
        },
        rangeLabel(vm = this) {
            const first = moment(vm.visibleWeekStarts[0], DATE_FORMAT)
            const last = moment(vm.visibleWeekStarts[VISIBLE_WEEKS - 1], DATE_FORMAT).add(6, 'days')
            return `${first.format('D MMM')} – ${last.format('D MMM')}`
        },
        heatmapRows(vm = this) {
            if (vm.scope === 'students') {
                const id = vm.rowStudentIds[0]
                const a = vm.assignments[id]
                if (!a) return []
                return a.combo.habits.map(habit => {
                    const cells = vm.visibleDates.map(({ date }) => vm.habitCell(id, habit, date))
                    const values = vm.visibleWeekStarts.map(start => vm.weekSummary(id, start)?.perHabit?.[habit.key]?.adherence)
                        .filter(v => v !== undefined && v !== null)
                    return { key: habit.key, name: vm.habitName(habit), cells, pct: vm.avg(values) }
                })
            }
            return vm.rowStudentIds.map(id => ({
                key: id,
                studentId: id,
                name: vm.studentNames[id] || id,
                cells: vm.visibleDates.map(({ date }) => vm.dayCell(id, date)),
                pct: vm.windowAdherence(id, vm.visibleWeekStarts),
            })).sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1))
        },
        evolutionStarts(vm = this) {
            return Array.from({ length: EVOLUTION_WEEKS }, (_, i) => moment(vm.currentWeekStart, DATE_FORMAT)
                .subtract((EVOLUTION_WEEKS - 1 - i) * 7, 'days').format(DATE_FORMAT))
        },
        evolutionWeeks(vm = this) {
            return vm.evolutionStarts.map(start => moment(start, DATE_FORMAT).format('D/M'))
        },
        evolutionGeneral(vm = this) {
            return vm.evolutionStarts.map(start => vm.avg(vm.rowStudentIds.map(id => vm.weekSummary(id, start)?.adherence)))
        },
        evolution(vm = this) {
            if (vm.metric === 'general') return vm.evolutionGeneral
            return vm.evolutionStarts.map(start => {
                const values = []
                vm.rowStudentIds.forEach(id => {
                    const summary = vm.weekSummary(id, start)
                    const habits = vm.weekHabits(id, start)
                    habits.filter(habit => vm.habitLabel(habit) === vm.metric)
                        .forEach(habit => values.push(summary?.perHabit?.[habit.key]?.adherence))
                })
                return vm.avg(values)
            })
        },
        metricItems(vm = this) {
            const labels = new Set()
            vm.rowStudentIds.forEach(id => (vm.assignments[id]?.combo?.habits || []).forEach(habit => labels.add(vm.habitLabel(habit))))
            return [{ value: 'general', text: vm.$t('habits.general_adherence') }, ...[...labels].map(label => ({ value: label, text: label }))]
        },
        metricLabel(vm = this) {
            return vm.metric === 'general' ? vm.$t('habits.general_adherence') : vm.metric
        },
        switchItems(vm = this) {
            return [
                { value: 'habit', name: vm.$t('habits.by_habit') },
                { value: 'weekday', name: vm.$t('habits.by_weekday') },
                { value: 'alarms', name: vm.$t('habits.alarms') },
            ]
        },
        switchTitle(vm = this) {
            return vm.switchItems.find(item => item.value === vm.switchView)?.name || ''
        },
        byHabit(vm = this) {
            const map = {}
            vm.rowStudentIds.forEach(id => vm.visibleWeekStarts.forEach(start => {
                const summary = vm.weekSummary(id, start)
                vm.weekHabits(id, start).forEach(habit => {
                    const value = summary?.perHabit?.[habit.key]?.adherence
                    if (value === undefined || value === null) return
                    const label = vm.habitLabel(habit)
                    if (!map[label]) map[label] = { label, values: [], habit, alarm: false }
                    map[label].values.push(value)
                    if (habit.alarm?.enabled) map[label].alarm = true
                })
            }))
            return Object.values(map).map(item => {
                const tags = []
                const f = item.habit.frequency
                if (f?.kind === 'weekly') tags.push(vm.$t('habits.per_week_tag', { n: f.times }))
                if (f?.kind === 'md') tags.push(mdLabel(f.offset))
                return { label: item.label, value: vm.avg(item.values), tags, alarm: item.alarm }
            }).sort((a, b) => a.value - b.value)
        },
        byWeekday(vm = this) {
            const buckets = Array.from({ length: 7 }, () => [])
            vm.visibleDates.forEach(({ date }) => {
                const day = moment(date, DATE_FORMAT).isoWeekday() - 1
                vm.rowStudentIds.forEach(id => {
                    const cell = vm.dayCell(id, date)
                    if (cell.status === 'value') buckets[day].push(Math.round(cell.score * 100))
                })
            })
            const items = buckets.map((values, i) => {
                const m = moment().isoWeekday(i + 1)
                return { label: m.format('dd').charAt(0).toUpperCase(), long: m.format('dddd'), value: vm.avg(values) }
            })
            const valid = items.filter(item => item.value !== null)
            const worst = valid.length > 1 ? valid.reduce((a, b) => b.value < a.value ? b : a) : null
            return items.map(item => ({ ...item, worst: worst && item === worst }))
        },
        worstDay(vm = this) {
            return vm.byWeekday.find(item => item.worst) || null
        },
        alarmStats(vm = this) {
            const stats = { perHabit: 0, daily: 0, none: 0 }
            vm.rowStudentIds.forEach(id => {
                const a = vm.assignments[id]
                const hours = resolveAlarmHours(a)
                if (!hours.length) stats.none++
                else if (a.studentPrefs?.singleReminder || a.alarmsOverride === 'daily' || a.combo.alarms?.mode === 'daily') stats.daily++
                else stats.perHabit++
            })
            return stats
        },
        histogram(vm = this) {
            const counts = Array(24).fill(0)
            const alarms = new Set()
            vm.rowStudentIds.forEach(id => {
                resolveAlarmHours(vm.assignments[id]).forEach(hour => alarms.add(hour))
                vm.visibleWeekStarts.forEach(start => {
                    (vm.weekSummary(id, start)?.hourHistogram || []).forEach((n, hour) => { counts[hour] += n })
                })
            })
            const hours = Array.from({ length: 18 }, (_, i) => i + 6)
            const max = Math.max(1, ...hours.map(h => counts[h]))
            return hours.map(hour => ({ hour, pct: Math.round((counts[hour] / max) * 100), alarm: alarms.has(hour) }))
        },
        kpis(vm = this) {
            const yesterday = moment(vm.today, DATE_FORMAT).subtract(1, 'days').format(DATE_FORMAT)
            const adherence = vm.avg(vm.rowStudentIds.map(id => vm.windowAdherence(id, vm.visibleWeekStarts)))
            const previous = vm.avg(vm.rowStudentIds.map(id => vm.windowAdherence(id, vm.previousWeekStarts)))
            const delta = adherence !== null && previous !== null ? adherence - previous : null
            const adherenceKpi = {
                label: vm.$t('habits.kpi_adherence'),
                value: adherence === null ? null : `${adherence}%`,
                delta: delta === null ? null : `${delta > 0 ? '+' : ''}${delta} pts`,
                deltaUp: delta === null ? null : delta >= 0,
                color: vm.levelColor(adherence),
            }
            if (vm.scope === 'students') {
                const id = vm.rowStudentIds[0]
                const a = vm.assignments[id]
                const last = vm.lastRecord(id)
                return [
                    adherenceKpi,
                    { label: vm.$t('habits.kpi_streak'), value: `🔥 ${vm.currentStreak(a, yesterday)}` },
                    { label: vm.$t('habits.kpi_active_habits'), value: a.combo.habits.length, hint: a.combo.name },
                    { label: vm.$t('habits.kpi_last_record'), value: last ? moment(last).format('D MMM') : null },
                ]
            }
            const active = vm.scopeIds.filter(id => isAssignmentActive(vm.assignments[id], vm.today))
            const streaks = vm.rowStudentIds.filter(id => vm.currentStreak(vm.assignments[id], yesterday) >= 7).length
            const limit = moment(vm.today, DATE_FORMAT).subtract(2, 'days').format(DATE_FORMAT)
            const silent = vm.rowStudentIds.filter(id => {
                const a = vm.assignments[id]
                if (!isAssignmentActive(a, vm.today) || a.startDate > limit) return false
                const last = vm.lastRecord(id)
                return !last || last < limit
            })
            return [
                adherenceKpi,
                { label: vm.$t('habits.kpi_with_combo'), value: `${active.length}/${vm.scopeIds.length}` },
                { label: vm.$t('habits.kpi_streaks'), value: streaks },
                {
                    label: vm.$t('habits.kpi_silent'),
                    value: silent.length,
                    color: silent.length ? '#E85C5C' : '',
                    hint: silent.slice(0, 3).map(id => (vm.studentNames[id] || '').split(' ')[0]).join(', ') + (silent.length > 3 ? '…' : ''),
                },
            ]
        },
    },
    async mounted() {
        await this.loadStudents()
    },
    methods: {
        ...mapMutations(['setLibraryTab']),
        avg(values) {
            const valid = values.filter(v => v !== null && v !== undefined)
            if (!valid.length) return null
            return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
        },
        level(value) {
            return adherenceLevel(value)
        },
        levelColor(value) {
            const level = adherenceLevel(value)
            if (level === 'good') return '#009966'
            if (level === 'mid') return '#E8C000'
            if (level === 'bad') return '#E85C5C'
            return ''
        },
        habitLabel(habit) {
            return this.habitName(habit)
        },
        setScope(scope) {
            const vm = this
            vm.scope = scope
            vm.comboFilter = ''
            vm.metric = 'general'
            vm.switchView = scope === 'students' ? 'weekday' : 'habit'
        },
        drill(studentId) {
            const vm = this
            vm.setScope('students')
            vm.currentStudent = vm.students.find(s => s.objectID === studentId) || null
        },
        goLibrary() {
            const vm = this
            vm.setLibraryTab('habits')
            vm.pushRoute({ name: vm.isCompany ? 'company.library' : 'coach.library' })
        },
        async loadStudents() {
            const vm = this
            if (!vm.coachId) return
            try {
                const { hits } = await UserIndex.search('', {
                    filters: `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`,
                    hitsPerPage: 1000,
                })
                vm.students = hits.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            } catch (err) {
                console.log(err)
            }
        },
        async loadData() {
            const vm = this
            const ids = vm.scopeIds.filter(id => !(id in vm.assignments))
            if (!ids.length) return
            await vm.withLoading(async () => {
                const from = moment(vm.currentWeekStart, DATE_FORMAT).subtract((WEEKS_LOADED + VISIBLE_WEEKS - 1) * 7, 'days').format(DATE_FORMAT)
                const groups = chunk(ids, 10)
                const [assignmentSnaps, weekSnaps] = await Promise.all([
                    Promise.all(groups.map(group => db.collection('habitAssignments').where('studentId', 'in', group).get())),
                    Promise.all(groups.map(group => db.collection('habitWeeks').where('studentId', 'in', group).where('weekStart', '>=', from).get())),
                ])
                const assignments = { ...vm.assignments }
                ids.forEach(id => { assignments[id] = null })
                assignmentSnaps.forEach(snap => snap.docs.forEach(doc => { assignments[doc.id] = { ...doc.data(), id: doc.id } }))
                const weeks = { ...vm.weeks }
                ids.forEach(id => { weeks[id] = {} })
                weekSnaps.forEach(snap => snap.docs.forEach(doc => {
                    const data = doc.data()
                    weeks[data.studentId] = { ...(weeks[data.studentId] || {}), [data.weekStart]: data }
                }))
                vm.assignments = assignments
                vm.weeks = weeks
            })
        },
        async shareImage() {
            const vm = this
            if (vm.loadingShare || !vm.rowStudentIds.length) return
            if (!vm.requirePlan('shareImage')) return
            const node = document.querySelector('[data-share-target="statistics"]')
            if (!node) return
            vm.loadingShare = true
            try {
                await shareNodeAsImage(node, {
                    title: vm.$t('habits.title'),
                    period: vm.rangeLabel,
                    subject: vm.scopeName,
                    isDark: vm.$vuetify.theme.dark,
                    filenameParts: [vm.$t('habits.title'), vm.rangeLabel, vm.scopeName],
                    dialogTitle: vm.$t('common.share_image'),
                    loadingText: vm.$t('common.generating_image'),
                })
            } catch (err) {
                console.log('shareImage error', err)
            } finally {
                vm.loadingShare = false
            }
        },
        onGoalsSaved(goals) {
            const vm = this
            const a = vm.studentAssignment
            if (a) vm.assignments = { ...vm.assignments, [a.id]: { ...a, goals } }
        },
        weekHabits(id, start) {
            return this.weeks[id]?.[start]?.habits || this.assignments[id]?.combo?.habits || []
        },
        inAssignment(id, date) {
            const a = this.assignments[id]
            if (!a) return false
            if (a.startDate && date < a.startDate) return false
            if (a.endDate && date > a.endDate) return false
            return date <= this.today
        },
        weekContext(id, date) {
            const vm = this
            const start = weekStartOf(date)
            const week = vm.weeks[id]?.[start]
            const a = vm.assignments[id]
            return {
                week,
                habits: week?.habits || a?.combo?.habits || [],
                goals: week?.goals || a?.goals || {},
                md: week?.mdDays || {},
            }
        },
        dayCell(id, date) {
            const vm = this
            if (!vm.inAssignment(id, date)) return { date, status: 'nodata', tooltip: '' }
            const { week, habits, goals, md } = vm.weekContext(id, date)
            const progress = dayProgress(habits, date, week?.days?.[date], md, goals)
            const label = moment(date, DATE_FORMAT).format('dddd D MMM')
            if (!progress.total) return { date, status: 'na', tooltip: `${label} · ${vm.$t('habits.not_requested')}` }
            const pct = Math.round(progress.score * 100)
            return {
                date,
                status: 'value',
                score: progress.score,
                color: scoreColor(progress.score),
                tooltip: `${label} · ${vm.$t('habits.x_of_y_done', { x: progress.done, y: progress.total })} (${pct}%)`,
            }
        },
        habitCell(id, habit, date) {
            const vm = this
            if (!vm.inAssignment(id, date)) return { date, status: 'nodata', tooltip: '' }
            const { week, goals, md } = vm.weekContext(id, date)
            const label = moment(date, DATE_FORMAT).format('dddd D MMM')
            const value = week?.days?.[date]?.values?.[habit.key]?.v
            if (!habitAppliesOn(habit, date, md)) return { date, status: 'na', tooltip: `${label} · ${vm.$t('habits.not_requested')}` }
            if (habit.frequency?.kind === 'weekly' && !habitDone(habit, value, goals)) return { date, status: 'na', tooltip: label }
            const score = habitScore(habit, value, goals)
            let detail
            if (habit.type === 'bool') detail = score >= 1 ? vm.$t('habits.did_it') : vm.$t('habits.did_not')
            else detail = `${vm.formatHabitNumber(value || 0)} / ${vm.habitGoalLabel(habit, goals)} (${Math.round(score * 100)}%)`
            return { date, status: 'value', score, color: scoreColor(score), tooltip: `${label} · ${detail}` }
        },
        weekSummary(id, start) {
            const vm = this
            const a = vm.assignments[id]
            if (!a) return null
            const end = moment(start, DATE_FORMAT).add(6, 'days').format(DATE_FORMAT)
            if ((a.startDate && end < a.startDate) || (a.endDate && start > a.endDate) || start > vm.today) return null
            const week = vm.weeks[id]?.[start]
            return computeWeekSummary({
                habits: week?.habits || a.combo.habits,
                goals: week?.goals || a.goals || {},
                days: week?.days || {},
                mdDays: week?.mdDays || {},
                weekStart: start,
                startDate: a.startDate,
                endDate: a.endDate,
                until: vm.today,
            })
        },
        windowAdherence(id, starts) {
            const vm = this
            return vm.avg(starts.map(start => vm.weekSummary(id, start)?.adherence))
        },
        lastRecord(id) {
            let last = null
            Object.values(this.weeks[id] || {}).forEach(week => Object.keys(week.days || {}).forEach(date => {
                if (Object.keys(week.days[date]?.values || {}).length && (!last || date > last)) last = date
            }))
            return last
        },
        currentStreak(a, yesterday) {
            if (!a?.streak?.count) return 0
            return a.streak.lastDate && a.streak.lastDate >= yesterday ? a.streak.count : 0
        },
        yOf(value) {
            return 140 - (value / 100) * 130
        },
        dots(series) {
            const vm = this
            const step = 320 / Math.max(1, series.length - 1)
            return series.map((value, i) => value === null ? null : { x: i * step, y: vm.yOf(value) }).filter(Boolean)
        },
        points(series) {
            return this.dots(series).map(p => `${p.x},${p.y}`).join(' ')
        },
    },
    watch: {
        scopeIds() {
            this.loadData()
        },
        coachId() {
            this.loadStudents()
        },
    },
}
</script>

<style scoped>
.habits-stats {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.hs-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
}

.export-btn.v-btn {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface2-base) !important;
    color: var(--v-txtPrimary-base) !important;
}

.export-btn.v-btn:hover {
    border-color: var(--v-accent-base);
}

.export-btn.v-btn.v-btn--disabled {
    border-color: var(--v-outline-base);
    color: var(--v-txtMuted-base) !important;
    opacity: .5;
}

.hs-select {
    width: 260px;
    max-width: 100%;
}

.hs-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 72px 16px;
    text-align: center;
    color: var(--v-txtMuted-base);
}

.hs-empty p {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
}

.hs-kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
}

.hs-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.hs-wide {
    grid-column: 1 / -1;
}

.hs-nav {
    display: flex;
    align-items: center;
    gap: 6px;
}

.hs-range {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    white-space: nowrap;
}

.hs-arrow {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.hs-arrow:disabled {
    opacity: .5;
    cursor: default;
}

.hs-muted {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-top: 8px;
}

.hm-scroll {
    overflow: auto;
    max-height: 320px;
}

.hm {
    border-collapse: separate;
    border-spacing: 2px;
    width: 100%;
}

.hm th {
    position: sticky;
    top: 0;
    background: var(--v-surface-base);
    z-index: 1;
}

.hm-name {
    text-align: left;
    font-size: 12px;
    color: var(--v-txtPrimary-base);
    max-width: 130px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 6px;
}

.hm-link {
    background: none;
    border: none;
    padding: 0;
    color: var(--v-accent-base);
    font-size: 12px;
    cursor: pointer;
    text-align: left;
}

.hm-day {
    font-size: 9px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    min-width: 16px;
}

.hm-day--week {
    border-left: 1px solid var(--v-outline-base);
}

.hm-day--today {
    color: var(--v-accent-base);
}

.hm-num {
    font-weight: 500;
}

.hm-td {
    padding: 0;
}

.hm-cell {
    display: block;
    width: 100%;
    min-width: 14px;
    height: 18px;
    border-radius: 4px;
    background: var(--v-surface2-base);
}

.hm-cell--na {
    background: transparent;
    border: 1px dashed var(--v-outline2-base);
}

.hm-pct {
    font-size: 11px;
    font-weight: 700;
    text-align: right;
    padding-left: 6px;
    white-space: nowrap;
}

.hs-lvl--good {
    color: #009966;
}

.hs-lvl--mid {
    color: #E8C000;
}

.hs-lvl--bad {
    color: #E85C5C;
}

.hs-lvl--none {
    color: var(--v-txtMuted-base);
}

.theme--light .hs-lvl--mid {
    color: #C8A000;
}

.hs-bg--good {
    background: #009966;
}

.hs-bg--mid {
    background: #E8C000;
}

.hs-bg--bad {
    background: #E85C5C;
}

.hs-bg--none {
    background: var(--v-outline2-base);
}

.evo {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.evo-svg {
    width: 100%;
    height: 180px;
    overflow: visible;
}

.evo-grid {
    stroke: var(--v-outline-base);
    stroke-width: 1;
    vector-effect: non-scaling-stroke;
}

.evo-goal {
    stroke: var(--v-txtMuted-base);
    stroke-dasharray: 4 4;
}

.evo-line {
    fill: none;
    stroke: var(--v-accent-base);
    stroke-width: 2;
    vector-effect: non-scaling-stroke;
}

.evo-line--bg {
    stroke: var(--v-outline2-base);
}

.evo-dot {
    fill: var(--v-accent-base);
}

.evo-x {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: var(--v-txtMuted-base);
}

.evo-leg {
    display: flex;
    gap: 14px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.evo-leg i {
    display: inline-block;
    width: 14px;
    height: 0;
    margin-right: 4px;
    vertical-align: middle;
}

.evo-leg-line {
    border-top: 2px solid var(--v-accent-base);
}

.evo-leg-goal {
    border-top: 2px dashed var(--v-txtMuted-base);
}

.bars {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
}

.bar-row {
    display: grid;
    grid-template-columns: 200px 1fr 44px;
    align-items: center;
    gap: 10px;
}

.bar-name {
    font-size: 12px;
    color: var(--v-txtPrimary-base);
}

.bar-name em {
    font-style: normal;
    font-size: 9px;
    font-weight: 700;
    margin-left: 4px;
    color: var(--v-accent-base);
}

.bar-name .v-icon {
    color: var(--v-accent-base) !important;
    margin-left: 2px;
}

.bar-track {
    height: 8px;
    border-radius: 4px;
    background: var(--v-surface2-base);
    overflow: hidden;
}

.bar-fill {
    height: 100%;
    border-radius: 4px;
}

.bar-val {
    font-size: 12px;
    font-weight: 700;
    text-align: right;
}

.dow-bars {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 10px;
    height: 180px;
}

.dow-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.dow-val {
    font-size: 11px;
    font-weight: 700;
    color: var(--v-txtSecondary-base);
}

.dow-track {
    flex: 1;
    width: 28px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    display: flex;
    align-items: flex-end;
    overflow: hidden;
}

.dow-fill {
    width: 100%;
    border-radius: 6px;
}

.dow-lbl {
    font-size: 11px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
}

.al-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.al-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 14px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.al-chip .v-icon {
    color: var(--v-accent-base) !important;
}

.al-title {
    margin: 14px 0 6px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: var(--v-txtMuted-base);
}

.al-hist {
    display: grid;
    grid-template-columns: repeat(18, 1fr);
    gap: 4px;
    height: 140px;
    align-items: end;
}

.al-col {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    gap: 2px;
}

.al-bar {
    width: 100%;
    min-height: 2px;
    border-radius: 3px 3px 0 0;
    background: var(--v-accent-base);
}

.al-bell {
    color: var(--v-accent-base) !important;
}

.al-h {
    font-size: 9px;
    color: var(--v-txtMuted-base);
}

@media (max-width: 960px) {
    .hs-kpis {
        grid-template-columns: repeat(2, 1fr);
    }

    .hs-grid {
        grid-template-columns: 1fr;
    }

    .bar-row {
        grid-template-columns: 120px 1fr 40px;
    }
}
</style>
